import { HTMLBnumFragment } from 'components/atoms';
import BnumElementInternal from 'components/bnum-element-states';
import { Define } from 'core/decorators';
import { Listen } from 'core/decorators/listen';
import style from './bnum-avatar-action.less';
import { Listener } from 'core/decorators/listener';
import JsEvent from '@rotomeca/event';
import { Nullable } from '@rotomeca/utils';
import { ValueOf } from 'core/utils/types';

const TEMPLATE = (
	<HTMLBnumFragment>
		<span id="avatar">
			<slot name="avatar"></slot>
		</span>
		<span id="action">
			<slot name="action"></slot>
		</span>
	</HTMLBnumFragment>
);

/**
 * Type de marque (*branded type*) garantissant qu'un `symbol` représente bien un état de {@link HTMLBnumAvatarAction}.
 * @see {@link STATES}
 */
type StateSymbol = symbol & { __branded: 'state' };

/**
 * Ensemble des états possibles d'un {@link HTMLBnumAvatarAction}.
 */
const STATES = {
	/** L'élément est inactif : ni survolé, ni forcé. */
	INACTIVE: Symbol() as StateSymbol,
	/** L'élément est survolé par la souris. */
	ACTIVE: Symbol() as StateSymbol,
	/** L'état d'action est forcé programmatiquement via {@link HTMLBnumAvatarAction.forceActionState}. */
	FORCED: Symbol() as StateSymbol,
	/** L'état d'action est forcé **et** l'élément est simultanément survolé. */
	FORCED_AND_ACTIVE: Symbol() as StateSymbol,
} as const;

/**
 * Union des valeurs de {@link STATES}.
 * @see {@link ValueOf}
 */
type HTMLBnumAvatarActionStates = ValueOf<typeof STATES>;

/**
 * Composant Web personnalisé affichant un avatar associé à une action contextuelle.
 *
 * @remarks
 * L'action est rendue visible au survol de l'élément (`mouseenter` / `mouseleave`)
 * ou sur demande explicite via {@link forceActionState}.
 * Deux slots nommés sont exposés : `avatar` et `action`.
 *
 * Hérite de {@link BnumElementInternal} qui gère le cycle de vie attach/detach.
 *
 * @example
 * ```html
 * <bnum-avatar-action>
 *   <img slot="avatar" src="user.png" />
 *   <button slot="action">Modifier</button>
 * </bnum-avatar-action>
 * ```
 *
 * @example
 * Création programmatique via {@link Create} :
 * ```typescript
 * const el = HTMLBnumAvatarAction.Create({ avatar: imgEl, action: btnEl });
 * document.body.appendChild(el);
 * ```
 *
 * @structure Exemple
 * <bnum-avatar-action><slot name="avatar"><img src="https://bnum.din.gouv.fr/skins/mel_elastic/images/bnumloader.svg" height="50" /></slot><slot name="action"><button>Click</button></slot></bnum-avatar-action>
 *
 * @slot avatar - Slot qui affichera l'avatar (avatar qui sera affiché par défaut)
 * @slot action - Elément qui sera affiché au survol de l'élément
 *
 * @state action - Actif si l'élément est survolé
 * @state forced - Actif si appelé par `forceActionState`
 *
 * @event {MouseEvent} mouseenter - Événement déclenché au survol
 * @event {MouseLeave} mouseenter - Événement déclenché au survol
 *
 */
@Define({ template: TEMPLATE, styles: style, tag: 'bnum-avatar-action' })
export class HTMLBnumAvatarAction extends BnumElementInternal {
	/** État interne courant de l'élément. */
	#_state: HTMLBnumAvatarActionStates = STATES.INACTIVE;

	/**
	 * Événement déclenché lorsque le curseur entre dans l'élément (`mouseenter`).
	 *
	 * @remarks
	 * Équivalent d'un `event` C# de type `EventHandler<T>`.
	 * @see {@link https://learn.microsoft.com/fr-fr/dotnet/csharp/programming-guide/events/ | Événements (Guide C#)}
	 * @event
	 */
	@Listener() accessor onEnter!: JsEvent<
		(caller: HTMLBnumAvatarAction) => void
	>;

	/**
	 * Événement déclenché lorsque le curseur quitte l'élément (`mouseleave`).
	 * @event
	 */
	@Listener() accessor onLeave!: JsEvent<
		(caller: HTMLBnumAvatarAction) => void
	>;

	/**
	 * Événement déclenché lors du détachement de l'élément du DOM.
	 * @event
	 */
	@Listener() accessor onDestroy!: JsEvent<
		(caller: HTMLBnumAvatarAction) => void
	>;

	/**
	 * État courant de l'élément parmi les valeurs de {@link STATES}.
	 */
	get state() {
		return this.#_state;
	}

	constructor() {
		super();
	}

	/**
	 * @inheritDoc
	 *
	 * @remarks
	 * Enregistre les écouteurs de survol après l'attachement au DOM.
	 */
	protected _p_attach(): void {
		super._p_attach();
		this.#_addListeners();
	}

	/**
	 * @inheritDoc
	 *
	 * @remarks
	 * Déclenche {@link onDestroy} si des abonnés sont enregistrés,
	 * puis vide la liste des abonnés.
	 */
	protected _p_detach(): void {
		if (this.onDestroy.haveEvents()) {
			this.onDestroy.call(this);
			this.onDestroy.events = {};
		}
	}

	/**
	 * Enregistre les gestionnaires d'événements souris sur l'élément.
	 * @returns `this` pour le chaînage.
	 */
	#_addListeners() {
		this._handleOnHover();
		this._handleOnLeave();
		return this;
	}

	/**
	 * Lie le gestionnaire {@link _handleOnMouseEnter} à l'événement `mouseenter`.
	 * @returns Le gestionnaire lié.
	 */
	@Listen('mouseenter')
	private _handleOnHover() {
		return this._handleOnMouseEnter;
	}

	/**
	 * Lie le gestionnaire {@link _handleOnMouseLeave} à l'événement `mouseleave`.
	 * @returns Le gestionnaire lié.
	 */
	@Listen('mouseleave')
	private _handleOnLeave() {
		return this._handleOnMouseLeave;
	}

	/**
	 * Gère l'entrée du curseur : déclenche {@link onEnter}, active le state CSS
	 * `action` et met à jour {@link state}.
	 */
	private _handleOnMouseEnter() {
		if (this.onEnter.haveEvents()) this.onEnter.call(this);

		this._p_addState('action');

		if (this.#_state === STATES.FORCED) this.#_state = STATES.FORCED_AND_ACTIVE;
		else this.#_state = STATES.ACTIVE;
	}

	/**
	 * Gère la sortie du curseur : déclenche {@link onLeave}, retire le state CSS
	 * `action` et met à jour {@link state}.
	 */
	private _handleOnMouseLeave() {
		if (this.onLeave.haveEvents()) this.onLeave.call(this);

		this._p_removeState('action');
		if (this.#_state === STATES.FORCED_AND_ACTIVE) this.#_state = STATES.FORCED;
		else this.#_state = STATES.INACTIVE;
	}

	/**
	 * Force ou retire programmatiquement l'état d'action visible, indépendamment du survol.
	 *
	 * @param params - Paramètres de la méthode.
	 * @param params.enabled - `true` pour forcer l'affichage de l'action, `false` pour le retirer.
	 *
	 * @remarks
	 * Quand `enabled` est `true`, le state CSS `forced` est ajouté et {@link state}
	 * passe à {@link STATES.FORCED} ou {@link STATES.FORCED_AND_ACTIVE} selon le survol courant.
	 * L'opération inverse retire le state `forced` et restaure l'état précédent.
	 */
	public forceActionState({ enabled }: { enabled: boolean }) {
		if (enabled) {
			this._p_addState('forced');

			if (this.#_state === STATES.ACTIVE)
				this.#_state = STATES.FORCED_AND_ACTIVE;
			else this.#_state = STATES.FORCED;
		} else {
			this._p_removeState('forced');

			if (this.#_state === STATES.FORCED_AND_ACTIVE)
				this.#_state = STATES.ACTIVE;
			else this.#_state = STATES.INACTIVE;
		}
	}

	/**
	 * Crée et initialise une instance de {@link HTMLBnumAvatarAction} via `document.createElement`.
	 *
	 * @param params - Paramètres optionnels de construction.
	 * @param params.avatar - Élément HTML à placer dans le slot `avatar`. Si l'attribut `slot` est absent, il est ajouté automatiquement.
	 * @param params.action - Élément HTML à placer dans le slot `action`. Si l'attribut `slot` est absent, il est ajouté automatiquement.
	 * @returns Une nouvelle instance de {@link HTMLBnumAvatarAction} prête à être insérée dans le DOM.
	 */
	static Create({
		avatar = null,
		action = null,
	}: {
		avatar?: Nullable<HTMLElement>;
		action?: Nullable<HTMLElement>;
	} = {}): HTMLBnumAvatarAction {
		const node = document.createElement(this.TAG) as HTMLBnumAvatarAction;

		if (avatar) {
			if (!avatar.hasAttribute('slot')) avatar.setAttribute('slot', 'avatar');
			node.appendChild(avatar);
		}

		if (action) {
			if (!action.hasAttribute('slot')) action.setAttribute('slot', 'action');
			node.appendChild(action);
		}

		return node;
	}

	/**
	 * Liste des états possibles du composant.
	 */
	static get States() {
		return STATES;
	}
}
